import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Sparkles, Database, TrendingUp, Users, Github, ExternalLink, Rocket } from 'lucide-react';
import { allProjects } from '../data/projectsData';
import AnimatedCard from '../components/AnimatedCard';
import FadeIn from '../components/FadeIn';

const ProjectsPage = () => {
  const navigate = useNavigate();

  const iconMap = {
    'Machine Learning': Sparkles,
    'Big Data': Database,
    'Predictive Analytics': TrendingUp,
    'Business Intelligence': Users,
    'Data Analytics': TrendingUp,
    'Database Engineering': Database,
    'Education': Sparkles,
    'Product': Rocket
  };

  return (
    <div className="min-h-screen bg-white dark:bg-black text-black dark:text-white py-20 px-6">
      <div className="max-w-6xl mx-auto">
        <FadeIn direction="down">
          <div className="mb-16">
            <h1 className="text-5xl md:text-6xl font-light mb-6 tracking-tight">All Projects</h1>
            <p className="text-xl text-gray-600 dark:text-gray-400">
              A collection of my work in AI/ML, Data Analytics, and Product Development
            </p>
          </div>
        </FadeIn>

        <div className="space-y-12">
          {allProjects.map((project, index) => {
            const Icon = iconMap[project.category] || Sparkles;
            return (
              <AnimatedCard key={index} delay={index * 0.1}>
                <Card 
                  className="group overflow-hidden border border-gray-200 dark:border-gray-800 hover:border-gray-400 dark:hover:border-gray-600 transition-all duration-300 cursor-pointer"
                  onClick={() => navigate(`/projects/${project.slug}`)}
                >
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="relative h-64 md:h-auto overflow-hidden bg-gray-100 dark:bg-gray-900">
                    <img 
                      src={project.image} 
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div>
                    <CardHeader>
                      <div className="flex items-start gap-3 mb-3">
                        <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-900">
                          <Icon size={24} />
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-2xl font-normal mb-2 leading-tight group-hover:text-gray-600 dark:group-hover:text-gray-400 transition-colors">
                            {project.title}
                          </CardTitle>
                          <Badge variant="secondary" className="text-xs font-normal">{project.category}</Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 dark:text-gray-400 mb-4">
                        {project.description}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {project.technologies.slice(0, 5).map((tech, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs font-normal">
                            {tech}
                          </Badge>
                        ))}
                        {project.technologies.length > 5 && (
                          <Badge variant="outline" className="text-xs font-normal">
                            +{project.technologies.length - 5} more
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="gap-2 px-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/projects/${project.slug}`);
                          }}
                        >
                          View Details
                          <ExternalLink size={14} />
                        </Button>
                        {(project.liveUrl || project.githubUrl) && (
                          <a
                            href={project.liveUrl || project.githubUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button variant="ghost" size="sm" className="gap-2 px-0">
                              {project.liveUrl ? <ExternalLink size={14} /> : <Github size={14} />}
                              {project.liveUrl ? 'View Project' : 'GitHub'}
                            </Button>
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </div>
                </div>
              </Card>
            </AnimatedCard>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProjectsPage;